/**
 * TEMPORARY BRIDGE. Delete this file when ENG-1584 (Move/Release popups) and
 * ENG-1585 (Assign/Remove popups) replace the legacy bulk popups in
 * `src/pages/Storage/modals/` with monolith-ui versions.
 *
 * Those popups read two props from the page that hosts them:
 * - `table`: the legacy `Monolith-UI/Table` instance, through
 *   `getVirtualSelectionTotal`, `getSelectionState`, `getSelectedRows`,
 *   `getExcludedRows` and `clearSelections`.
 * - `query`: the legacy `{ query: { conditions, order, search, case_id } }`
 *   envelope, spread into the bulk request so the server can re-resolve an
 *   "all" or "someExcluded" selection.
 *
 * This hook builds both from the monolith-ui `StorageTable` state, so the
 * popups mount unchanged on top of it.
 *
 * Typing: the JS popups take both props untyped. `RemoveItemsPopup.tsx`
 * types them as the full legacy `useTable()` return (which also has
 * `setTableInstance`, `toggleCompact`, `toggleStripes`, `isCompact`,
 * `isStriped`, `onColumnResize`, none of which it calls) and as
 * `SharedCaseTabListQuery` (`Condition[]` conditions and a string
 * `case_id`). The runtime shapes match what it reads, and the server takes
 * a numeric `case_id`, so that call site casts both props
 * (`as unknown as ...`) rather than this hook widening its types.
 */
import { useMemo, useRef } from "react";
import {
  FilterDefinition,
  SelectionState,
  SelectionStatus,
} from "@monolith-forensics/monolith-ui";

import { resolveStorageQuery } from "@/Features/Storage/Tables/resolveStorageQuery";
import type {
  GetStorageQuery,
  StorageItem,
} from "@/Features/Storage/Tables/types";
import type { LegacyStorageQuery } from "@/Features/Storage/storage.api.types";

/** The subset of the legacy `Monolith-UI/Table` instance the popups call. */
export interface LegacyStoragePopupTable {
  getVirtualSelectionTotal: () => number;
  getSelectionState: () => `${SelectionStatus}`;
  getSelectedRows: () => StorageItem[];
  getExcludedRows: () => Array<Pick<StorageItem, "uuid">>;
  clearSelections: () => void;
}

/**
 * The legacy query envelope without paging. The bulk endpoints re-resolve a
 * selection against the whole matching set, so a page or page size would cut
 * that set down to one page.
 */
export interface LegacyStoragePopupQuery {
  query: Omit<LegacyStorageQuery["query"], "page" | "pageSize">;
}

export interface UseLegacyStoragePopupAdapterOptions {
  selectionState: SelectionState | undefined;
  /** The rows loaded so far (`StorageTableHandle.getRecords()`). */
  records: StorageItem[];
  /** `StorageTableHandle.getResolvedQuery()`. */
  resolvedQuery: GetStorageQuery;
  filterDefinitions: FilterDefinition[];
  clearSelections: () => void;
}

export const useLegacyStoragePopupAdapter = ({
  selectionState,
  records,
  resolvedQuery,
  filterDefinitions,
  clearSelections,
}: UseLegacyStoragePopupAdapterOptions): {
  table: LegacyStoragePopupTable;
  query: LegacyStoragePopupQuery;
} => {
  // Explicit selections survive a filter or search change (see
  // StorageTable), so a selected row can drop out of `records` after the
  // refetch. The legacy table kept the row objects it was handed, so keep
  // every selected row seen so far; otherwise Move and Release would silently
  // skip rows the user picked. The memo below writes this ref during render,
  // which is safe only while that write is idempotent (rebuilt from the same
  // inputs each time): keep it that way, since React may run it twice.
  const selectedRowCache = useRef(new Map<string, StorageItem>());

  const selectedRows = useMemo(() => {
    // Explicit rows only for "someIncluded"; in "all" and "someExcluded" the
    // server resolves the set from the query and the exclusions. Deliberate
    // on two counts: mfui's select all fills `selectedRowKeys` with every
    // loaded key, and the legacy table kept stale rows in `selectedRows` when
    // select all followed explicit checks. Returning [] matches the legacy
    // table for select all from nothing, and sends no stray `included` uuids.
    if (selectionState?.selectionStatus !== SelectionStatus.SomeIncluded) {
      selectedRowCache.current = new Map();
      return [];
    }
    const keys = selectionState.selectedRowKeys ?? [];
    const loaded = new Map(records.map((row) => [row.uuid, row]));
    const next = new Map<string, StorageItem>();
    for (const key of keys) {
      const row = loaded.get(key) ?? selectedRowCache.current.get(key);
      if (row) next.set(key, row);
    }
    selectedRowCache.current = next;
    return Array.from(next.values());
  }, [selectionState, records]);

  const query = useMemo<LegacyStoragePopupQuery>(() => {
    const {
      query: { page: _page, pageSize: _pageSize, ...rest },
    } = resolveStorageQuery({ resolvedQuery, page: 1, filterDefinitions });
    return { query: rest };
  }, [resolvedQuery, filterDefinitions]);

  const table = useMemo<LegacyStoragePopupTable>(
    () => ({
      getVirtualSelectionTotal: () => selectionState?.totalSelected ?? 0,
      getSelectionState: () =>
        selectionState?.selectionStatus ?? SelectionStatus.None,
      getSelectedRows: () => selectedRows,
      // The popups only read `uuid` off an excluded row.
      getExcludedRows: () =>
        (selectionState?.excludedRowKeys ?? []).map((uuid) => ({ uuid })),
      clearSelections,
    }),
    [selectionState, selectedRows, clearSelections]
  );

  return useMemo(() => ({ table, query }), [table, query]);
};
